import React from 'react'
import { MDXProvider } from '@mdx-js/react'
import { Alert, Box, Paper, Typography } from '@mui/material'
import {
  Info,
  Warning,
  Success,
  Step,
  FeatureBadge,
  ActionBadge,
  WarningBadge,
  SuccessBadge,
  DangerBadge,
} from './DocsComponents'
import DocLink from './DocLink'

export const docsMDXComponents = {
  Info,
  Warning,
  Success,
  Step,
  DocLink,
  FeatureBadge,
  ActionBadge,
  WarningBadge,
  SuccessBadge,
  DangerBadge,
  h1: (props) => (
    <Typography
      variant="title2"
      component="h1"
      gutterBottom
      sx={{
        letterSpacing: 0,
        color: '#1A2230',
        mb: 2.5,
        lineHeight: 1.15,
        fontWeight: 700,
      }}
      {...props}
    />
  ),

  h2: (props) => (
    <Typography
      gutterBottom
      component="h2"
      variant="title3"
      sx={{
        fontSize: '1.55rem',
        fontWeight: 700,
        letterSpacing: 0,
        color: '#31425a',
        mt: 5,
        mb: 2,
        position: 'relative',
      }}
      {...props}
    />
  ),

  h3: (props) => (
    <Typography
      component="h3"
      variant="title3"
      gutterBottom
      sx={{
        fontSize: '1.15rem',
        fontWeight: 700,
        color: '#1F3D7C',
        mt: 3.5,
        mb: 1.5,
      }}
      {...props}
    />
  ),

  p: (props) => (
    <Typography
      component="p"
      sx={{
        color: '#475569',
        lineHeight: 1.8,
        fontSize: { xs: '0.95rem', md: '1rem' },
        mb: 2.25,
      }}
      {...props}
    />
  ),

  ul: (props) => (
    <Box
      component="ul"
      sx={{
        pl: 3,
        mb: 3,
        '& li::marker': {
          color: '#239AA4',
        },
      }}
      {...props}
    />
  ),

  ol: (props) => (
    <Box
      component="ol"
      sx={{
        pl: 3,
        mb: 3,
        '& li::marker': {
          color: '#239AA4',
          fontWeight: 900,
        },
      }}
      {...props}
    />
  ),

  li: (props) => (
    <Typography
      component="li"
      sx={{
        mb: 1,
        color: '#475569',
        lineHeight: 1.75,
      }}
      {...props}
    />
  ),

  blockquote: (props) => (
    <Alert
      severity="info"
      sx={{
        my: 3,
        borderRadius: 3,
        border: '1px solid rgba(35,154,164,0.18)',
        bgcolor: 'rgba(35,154,164,0.07)',
        color: '#1A2230',
        '& .MuiAlert-icon': {
          color: '#239AA4',
        },
      }}
      {...props}
    />
  ),

  code: (props) => (
    <Box
      component="code"
      sx={{
        px: 0.75,
        py: 0.25,
        borderRadius: 1.2,
        bgcolor: '#F1F5F9',
        color: '#D4145A',
        fontSize: '0.9em',
        fontWeight: 700,
      }}
      {...props}
    />
  ),

  pre: (props) => (
    <Paper
      component="pre"
      elevation={0}
      sx={{
        my: 3,
        p: 2.5,
        borderRadius: 3,
        overflow: 'auto',
        bgcolor: '#101827',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
        '& code': {
          bgcolor: 'transparent',
          color: 'inherit',
          p: 0,
          fontWeight: 500,
        },
      }}
      {...props}
    />
  ),

  a: (props) => (
    <Box
      component="a"
      sx={{
        color: '#239AA4',
        fontWeight: 900,
        textDecoration: 'none',
        borderBottom: '1px solid rgba(35,154,164,0.28)',
        '&:hover': {
          borderBottomColor: '#239AA4',
        },
      }}
      {...props}
    />
  ),

  hr: () => (
    <Box
      sx={{
        position: 'relative',
        my: 5,
        height: '1px',
        width: '100%',

        background:
          'linear-gradient(90deg, rgba(35,154,164,0) 0%, rgba(35,154,164,0.55) 50%, rgba(35,154,164,0) 100%)',

        '&::after': {
          content: '""',
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 180,
          height: 12,
          borderRadius: '50%',
          background: 'rgba(35,154,164,0.12)',
          filter: 'blur(14px)',
          pointerEvents: 'none',
        },
      }}
    />
  ),
}

export default function DocsMDXProvider({ children }) {
  return <MDXProvider components={docsMDXComponents}>{children}</MDXProvider>
}
